import { apiClient } from "./browser";
import type { components } from "./generated/wio-veyra-api";
import type { CommandOptions } from "./suppliers";

export type EventSchemas = components["schemas"];
export type BuyerEventOverview = EventSchemas["BuyerEventOverview"];
export type EventCompany = EventSchemas["EventCompany"];
export type EventRecipient = EventSchemas["EventRecipientView"];
export type EligibleEventRecipient = EventSchemas["EligibleEventRecipient"];
export type SupplierEventDetail = EventSchemas["SupplierEventDetail"];
export type EventSnapshot = EventSchemas["SupplierEventSnapshot"];
export type EventReceipt = EventSchemas["EventReceipt"];
export type EventResult =
  | EventSchemas["EventCommandResult"]
  | EventSchemas["EventReopenResult"]
  | EventSchemas["RoundStartResult"];
export type EventBinding = {
  audience: "workspace" | "supplier";
  tenantId: string;
  accountId: string;
  supplierId?: string;
  accessId?: string;
};

export function eventPath(
  binding: EventBinding,
  eventId?: string,
  suffix = "",
) {
  const tenant = `/tenants/${encodeURIComponent(binding.tenantId)}` as const;
  if (binding.audience === "supplier") {
    if (!binding.supplierId || !binding.accessId)
      throw new Error("Missing supplier binding");
    return `${tenant}/supplier-portal/suppliers/${encodeURIComponent(binding.supplierId)}/events${eventId ? `/${encodeURIComponent(eventId)}` : ""}${suffix}` as const;
  }
  if (!eventId) throw new Error("Missing event locator");
  return `${tenant}/sourcing/${encodeURIComponent(eventId)}/participation${suffix}` as const;
}

export function eventQuery<T>(
  binding: EventBinding,
  eventId: string | undefined,
  suffix: string,
  signal: AbortSignal,
) {
  return apiClient.request<T>({
    operation: "ReadEventParticipation",
    method: "GET",
    kind: "query",
    path: eventPath(binding, eventId, suffix),
    signal,
  });
}

type EventInput =
  | EventSchemas["EventInviteInput"]
  | EventSchemas["EventRevokeInput"]
  | EventSchemas["EventResendInput"]
  | EventSchemas["EventReopenInput"]
  | EventSchemas["RoundStartInput"]
  | EventSchemas["SupplierDecisionInput"]
  | EventSchemas["SupplierAcknowledgementInput"]
  | EventSchemas["BuyerMessageInput"]
  | EventSchemas["SupplierMessageInput"];

export function eventCommand(
  binding: EventBinding,
  eventId: string,
  suffix: string,
  options: CommandOptions,
  body: EventInput,
) {
  return apiClient.request<EventResult, EventInput>({
    operation: "WriteEventParticipation",
    method: "POST",
    kind: "command",
    path: eventPath(binding, eventId, suffix),
    body,
    signal: options.signal,
    antiforgeryToken: options.csrfToken,
    idempotencyKey: options.idempotencyKey,
  });
}

export function supplierOperationQuery(
  binding: EventBinding,
  scope: { eventId: string; grantId: string },
  type: string,
  key: string,
  signal: AbortSignal,
) {
  const query = new URLSearchParams({
    grantId: scope.grantId,
    operationType: type,
    key,
  });
  if (type === "auction.document.prepare" || type === "auction.document.freeze")
    return eventQuery<EventSchemas["AuctionDocumentOperation"]>(
      binding,
      scope.eventId,
      `/auction/documents/operations/by-key?${query}`,
      signal,
    );
  if (type === "auction.lot.bid.submit")
    return eventQuery<EventSchemas["AuctionLotBidOperation"]>(
      binding,
      scope.eventId,
      `/auction/lot-bids/operations/by-key?${query}`,
      signal,
    );
  if (type === "auction.step.accept")
    return eventQuery<EventSchemas["AuctionStepOperation"]>(
      binding,
      scope.eventId,
      `/auction/steps/operations/by-key?${query}`,
      signal,
    );
  if (type === "auction.bid.submit")
    return eventQuery<EventSchemas["AuctionBidOperation"]>(
      binding,
      scope.eventId,
      `/auction/bids/operations/by-key?${query}`,
      signal,
    );
  if (type === "auction.rules.accept")
    return eventQuery<EventSchemas["AuctionAcceptanceOperation"]>(
      binding,
      scope.eventId,
      `/auction/operations/by-key?${query}`,
      signal,
    );
  return eventQuery<EventSchemas["SupplierOperationResponse"]>(
    binding,
    scope.eventId,
    `/operations/by-key?${query}`,
    signal,
  );
}
